// frontend/src/api/api.js
//
// Small fetch wrapper + auth helpers used across the app.
// Default API base points at your production domain; can be overridden
// with REACT_APP_API_BASE_URL at build time.
//
// NOTE: Backend is Django/DRF. Trailing slashes matter for POSTs:
// without a trailing slash APPEND_SLASH can redirect and drop request bodies.

function getApiBase() {
  const raw = (process.env.REACT_APP_API_BASE_URL || "").trim();
  const base = raw || "https://api.propelhabitpoints.org";
  return base.replace(/\/+$/, ""); // trim trailing slash
}

async function parseJsonSafe(res) {
  const text = await res.text().catch(() => "");
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}

function friendlyNetworkError(e) {
  const msg = (e && e.message) ? e.message : String(e || "Network error");
  if (/Failed to fetch|NetworkError|TypeError/i.test(msg)) {
    return "Network error. If this persists, it's likely CORS/SSL or the API host.";
  }
  return msg;
}

const ACCESS_KEY  = "ph_tokens_access";
const REFRESH_KEY = "ph_tokens_refresh";

export function getAccessToken() {
  try { return localStorage.getItem(ACCESS_KEY) || ""; } catch { return ""; }
}
export function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY) || ""; } catch { return ""; }
}

function normalizeTokens(src = {}) {
  const access =
    src.access ??
    src.access_token ??
    src.token ??
    src.jwt ??
    src.accessToken ??
    "";
  const refresh =
    src.refresh ??
    src.refresh_token ??
    src.refreshToken ??
    "";
  return { access: access || "", refresh: refresh || "" };
}

export function setTokens(tokens) {
  const { access, refresh } = normalizeTokens(tokens);
  try {
    if (access)  localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch {}
}

export function clearTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {}
}

export async function apiFetch(path, options = {}) {
  const base = getApiBase();
  const url  = `${base}${path.startsWith("/") ? "" : "/"}${path}`;

  const headers = new Headers(options.headers || {});
  headers.set("Accept", "application/json");
  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }

  const access = getAccessToken();
  if (access) headers.set("Authorization", `Bearer ${access}`);

  try {
    const res = await fetch(url, {
      ...options,
      headers,
      // Default is omit (JWT flow). Allow override by passing options.credentials.
      credentials: options.credentials ?? "omit",
    });

    const data = await parseJsonSafe(res);

    if (!res.ok) {
      const err = new Error(
        data?.detail || data?.message || data?.error || `HTTP ${res.status}`
      );
      err.status = res.status;
      err.data = data;
      throw err;
    }

    return data;
  } catch (e) {
    // ✅ Preserve HTTP errors (they have status/data)
    if (e && typeof e === "object" && "status" in e) {
      throw e;
    }
    // ✅ Friendly-wrap true network/CORS/SSL errors
    const err = new Error(friendlyNetworkError(e));
    err.cause = e;
    throw err;
  }
}

// ---- Auth helpers (used by Login etc.) ----

export async function login({ email, password }) {
  // Refactored backend: /api/auth/login/
  const data = await apiFetch("/api/auth/login/", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

  // Store tokens if present (some pages rely on this side-effect)
  const { access, refresh } = normalizeTokens(data);
  if (access || refresh) setTokens({ access, refresh });

  return data;
}

export async function refreshToken() {
  const refresh = getRefreshToken();
  if (!refresh) throw new Error("No refresh token");

  // Refactored backend: /api/auth/refresh/
  const data = await apiFetch("/api/auth/refresh/", {
    method: "POST",
    body: JSON.stringify({ refresh }),
  });

  const { access, refresh: newRefresh } = normalizeTokens(data);
  if (access || newRefresh) setTokens({ access, refresh: newRefresh || refresh });

  return data;
}

export async function getProfile() {
  // Refactored backend: /api/auth/me/
  return apiFetch("/api/auth/me/");
}

// ---- Signup flow used by src/pages/Signup.js ----
// This matches the import:  import { signupRequest } from '../api/api'
export async function signupRequest(payload) {
  // payload: { email, password, desired_role, first_name, last_name, grade?, house? }
  // Refactored backend route: /api/auth/signup-request/
  try {
    const res = await apiFetch("/api/auth/signup-request/", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    // apiFetch throws on non-2xx, so reaching here means success (2xx)
    return { ok: true, message: res?.detail || res?.message || "Request submitted.", data: res };
  } catch (e) {
    // If it's an HTTP error, try to surface backend message body too
    const message =
      e?.data?.detail ||
      e?.data?.message ||
      e?.data?.error ||
      e?.message ||
      "Signup failed.";
    return { ok: false, message };
  }
}
